<template>
  <div class="flex flex-col gap-4">
    <div class="sticky top-0 z-10 bg-white">
      <h1 class="text-left text-xl font-medium text-primary">
        Metrics
      </h1>

      <section class="mx-auto mt-4 w-[98%]">
        <InputWithIcon
          :val="metricQuery"
          classes="py-2 text-sm rounded-lg"
          placeholder="Search metrics..."
          @input="metricQuery = $event"
        >
          <template #icon>
            <SearchIcon />
          </template>
        </InputWithIcon>
      </section>
      <!-- Selected metrics summary -->
      <section class="mt-4 flex items-center gap-3.5 text-sm">
        <div class="flex items-center gap-1.5">
          <input
            id="select-all-metrics-checkbox"
            ref="select-all-metrics-checkbox"
            type="checkbox"
            class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
            @change="toggleCheckAllMetrics($event)"
          >
          <label
            for="select-all-metrics-checkbox"
            class="text-[#68737D]"
          >Select All</label>
        </div>
        <span class="text-[#68737D]">{{ selectedMetrics.length }} Selected</span>
      </section>
    </div>
    <section class="grow">
      <ul
        v-if="filteredTopicMetrics.length"
        class="flex w-full flex-col gap-3"
      >
        <li
          v-for="(
            { id, title, theme, isExpanded }, index
          ) in filteredTopicMetrics"
          :key="`${theme}-${title}`"
          class="esg-topic-item"
        >
          <div
            class="flex items-center justify-between gap-2 font-medium marker:content-none hover:cursor-pointer"
          >
            <div class="flex items-center gap-1.5">
              <input
                :id="`check-${id}`"
                v-model="filteredTopicMetrics[index].checked"
                type="checkbox"
                class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
                @change="toggleCheckAllMetricsByTopic($event, index)"
              >
              <label class="flex items-center gap-1.5 text-[#68737D]">
                <span>{{ theme }}</span>
                <ChevronDownLarge custom-style="h-4 w-4 -rotate-90" />
                <span
                  :title="title"
                  class="line-clamp-1"
                >{{ title }}</span>
              </label>
            </div>
            <button
              :id="`button-topic-${id}`"
              type="button"
              :title="isExpanded ? 'Hide' : 'Expand'"
              class="font-inter rounded bg-transparent px-4 py-1 text-gray-500"
              @click="toggleMetrics(index)"
            >
              <ChevronDownLarge
                :custom-style="`h-5 w-5 ${isExpanded ? 'rotate-180' : ''}`"
              />
            </button>
          </div>
          <ul
            v-if="filteredTopicMetrics[index].isExpanded"
            class="whitespace-nowrap px-6"
          >
            <li
              v-for="({ name, metric_id }, metricIndex) in filteredTopicMetrics[
                index
              ].metrics"
              :key="metric_id"
              class="esg-metric-item"
            >
              <input
                v-model="
                  filteredTopicMetrics[index].metrics[metricIndex].checked
                "
                type="checkbox"
                class="cursor-pointer rounded-sm border-2 border-gray-400 bg-white p-1.5 text-main-primary hover:border-gray-500 focus:border-main-primary focus:ring-transparent"
                @change="toggleCheckMetric($event, index, metricIndex)"
              >
              {{ name }}
            </li>
          </ul>
        </li>
      </ul>

      <div
        v-else
        class="flex h-full w-full items-center justify-center rounded border-2 border-dashed border-[#D4D4D4] bg-[#F8FAFC] px-2 py-16 text-center"
      >
        <span class="text-sm text-gray-400">Select Topic(s) to view Metrics
        </span>
      </div>
    </section>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { savedMetricsInjectionKey } from '@/utils/keys'

export default {
  name: 'EsgMetricsPicker',

  inject: { savedMetricsInjectionKey },

  props: {
    topicsMetrics: {
      type: Array,
      required: true,
    },
    savedMetrics: {
      type: Array,
      default() {
        return []
      },
    },
  },

  emits: ['metricsSelected'],

  data() {
    return {
      filteredTopicMetrics: this.transformTopicMetrics(this.topicsMetrics),
      allTopicMetrics: this.transformTopicMetrics(this.topicsMetrics),
      selectedMetrics: [],
      alreadySavedMetrics: this.savedMetrics.length
        ? this.savedMetrics
        : this.savedMetricsInjectionKey,
      metricQuery: '',
    }
  },

  watch: {
    topicsMetrics: {
      deep: true,
      handler: debounce(function (newTopicMetrics, oldTopicMetrics) {
        if (!newTopicMetrics.length && oldTopicMetrics.length) {
          this.alreadySavedMetrics = []
        }

        const filteredTopicMetrics = this.transformTopicMetrics(
          newTopicMetrics,
          !oldTopicMetrics.length,
        )

        const selectedMetrics = []

        if (oldTopicMetrics.length) {
        // Check previously checked metrics after toggling topics: updates selectedMetrics
          filteredTopicMetrics.forEach((topicMetric, index) => {
            topicMetric.metrics.forEach((metric) => {
              const isPreviouslySelected
              = this.selectedMetrics.findIndex(
                ({ metric_id }) => metric.metric_id === metric_id,
              ) !== -1
              const { title, id, theme } = filteredTopicMetrics[index]
              const esgMetric = { ...metric, topic: title, topic_id: id, theme }

              if (esgMetric.checked && isPreviouslySelected) {
                delete esgMetric.checked
                selectedMetrics.push(esgMetric)
              }
            })
          })
        }

        // Instead of mutating directly, replace the whole array
        this.selectedMetrics = [...selectedMetrics]
        this.filteredTopicMetrics = [...filteredTopicMetrics]
        this.allTopicMetrics = [...filteredTopicMetrics]

        // Check from the saved API data only the first visit: updates selectedMetrics
        if (!oldTopicMetrics.length) {
          const updatedSelectedMetrics = [...this.selectedMetrics]

          this.filteredTopicMetrics.forEach((topicMetric, index) => {
            topicMetric.metrics.forEach((metric) => {
              const metricIndex = this.selectedMetrics.findIndex(({ metric_id }) => metric.metric_id === metric_id)

              if (this.alreadySavedMetrics.includes(metric.metric_id) && metricIndex === -1) {
                const { title, id, theme } = this.filteredTopicMetrics[index]
                const esgMetric = {
                  ...metric,
                  topic: title,
                  topic_id: id,
                  theme,
                }

                delete esgMetric.checked
                updatedSelectedMetrics.push(esgMetric)
              }
            })
          })

          // Replace the whole array
          this.selectedMetrics = [...updatedSelectedMetrics]
        }

        nextTick(() => this.updateSelectAllChecker())
      }, 0),
    },
    metricQuery(newQuery, oldQuery) {
      if (!oldQuery && newQuery) {
        // Copy existing filtered to get the checked items
        this.allTopicMetrics = this.filteredTopicMetrics
      }
      if (!newQuery) {
        this.filteredTopicMetrics = this.allTopicMetrics
      }
      else {
        this.filteredTopicMetrics = [...this.allTopicMetrics].filter(({ title, metrics, theme }) =>
          title.toLowerCase().includes(newQuery.toLowerCase())
          || theme.toLowerCase().includes(newQuery.toLowerCase())
          || metrics.some(({ name }) => name.toLowerCase().includes(newQuery.toLowerCase())),
        )
      }
    },
    selectedMetrics: {
      deep: true,
      handler(newMetrics) {
        this.$emit('metricsSelected', newMetrics)
      },
    },
  },

  mounted() {
    nextTick(() => this.updateSelectAllChecker())
  },

  methods: {
    transformTopicMetrics(topicMetrics, firstVisit = true) {
      const grouped = groupBy(topicMetrics, 'theme')

      if (!topicMetrics.length) {
        return []
      }

      return [
        ...(grouped.Environment ?? []),
        ...(grouped?.Social ?? []),
        ...(grouped?.Governance ?? []),
      ].map((topic) => {
        topic.isExpanded = true
        topic.metrics = topic.metrics.map((metric) => {
          const isPreviouslySelected
            = this.selectedMetrics?.findIndex(
              ({ metric_id }) => metric_id === metric.metric_id,
            ) !== -1
          metric.checked = firstVisit
            ? this.alreadySavedMetrics?.includes(metric.metric_id)
            : isPreviouslySelected
          return metric
        })
        topic.checked = !topic.metrics.length
          ? false
          : topic.metrics.every(({ checked }) => checked)
        return topic
      })
    },
    toggleMetrics(index) {
      this.filteredTopicMetrics[index].isExpanded
        = !this.filteredTopicMetrics[index].isExpanded
      getCurrentInstance()?.proxy?.$forceUpdate()
    },
    toggleCheckAllMetrics(event) {
      if (this.metricQuery) {
        this.transformDataForAllMetrics(this.allTopicMetrics, event)
      }

      this.transformDataForAllMetrics(this.filteredTopicMetrics, event)
    },
    transformDataForAllMetrics(list, event) {
      list = list.map((topicMetric, index) => {
        topicMetric.checked = event.target.checked
        topicMetric.metrics = topicMetric.metrics.map((metric) => {
          metric.checked = event.target.checked
          const toggledMetric = {
            ...metric,
            topic: list[index].title,
            topic_id: list[index].id,
            theme: list[index].theme,
          }
          const toggledMetricIndex = this.selectedMetrics.findIndex(
            ({ metric_id }) => toggledMetric.metric_id === metric_id,
          )

          if (event.target.checked && toggledMetricIndex === -1) {
            delete toggledMetric.checked
            this.selectedMetrics.push(toggledMetric)
          }
          else if (!event.target.checked && toggledMetricIndex !== -1) {
            this.selectedMetrics.splice(toggledMetricIndex, 1)
          }

          return metric
        })
        return topicMetric
      })
    },
    toggleCheckAllMetricsByTopic(event, index) {
      if (this.metricQuery) {
        // Find the corresponding topicIndex in allTopicMetrics, so our search can be clean
        const allDataTopicIndex = this.allTopicMetrics.findIndex(
          ({ id }) => id === this.filteredTopicMetrics[index].id,
        )

        this.transformDataForMetricsByTopic(
          this.allTopicMetrics,
          event,
          allDataTopicIndex,
        )
      }

      this.transformDataForMetricsByTopic(
        this.filteredTopicMetrics,
        event,
        index,
      )

      this.updateSelectAllChecker()
    },
    transformDataForMetricsByTopic(list, event, index) {
      list[index].metrics = list[index].metrics.map((metric) => {
        metric.checked = event.target.checked

        this.updateSelectedMetrics(event, { ...metric }, index, list)

        return metric
      })
    },
    updateSelectedMetrics(event, toggledMetric, topicIndex, list) {
      const toggledMetricIndex = this.selectedMetrics.findIndex(
        ({ metric_id }) => toggledMetric.metric_id === metric_id,
      )

      toggledMetric = {
        ...toggledMetric,
        topic: list[topicIndex].title,
        topic_id: list[topicIndex].id,
        theme: list[topicIndex].theme,
      }

      if (event.target.checked && toggledMetricIndex === -1) {
        delete toggledMetric.checked
        this.selectedMetrics.push(toggledMetric)
      }
      else if (!event.target.checked && toggledMetricIndex !== -1) {
        this.selectedMetrics.splice(toggledMetricIndex, 1)
      }
    },
    toggleCheckMetric(event, topicIndex, metricIndex) {
      if (this.metricQuery) {
        // Find the corresponding topicIndex & metricIndex in allTopicMetrics
        const allDataTopicIndex = this.allTopicMetrics.findIndex(
          ({ id }) => id === this.filteredTopicMetrics[topicIndex].id,
        )
        const { metric_id: id }
          = this.filteredTopicMetrics[topicIndex].metrics[metricIndex]
        const allDataMetricIndex = this.allTopicMetrics[
          allDataTopicIndex
        ].metrics.findIndex(({ metric_id }) => metric_id === id)

        this.transformDataForCheckedMetric(
          this.allTopicMetrics,
          event,
          allDataTopicIndex,
          allDataMetricIndex,
        )
      }

      this.transformDataForCheckedMetric(
        this.filteredTopicMetrics,
        event,
        topicIndex,
        metricIndex,
      )

      this.updateSelectAllChecker()
    },
    transformDataForCheckedMetric(list, event, topicIndex, metricIndex) {
      list[topicIndex].metrics[metricIndex].checked = event.target.checked

      const toggledMetric = { ...list[topicIndex].metrics[metricIndex] }

      this.updateSelectedMetrics(event, toggledMetric, topicIndex, list)

      list[topicIndex].checked = !list[topicIndex].metrics.length
        ? false
        : list[topicIndex].metrics.every(({ checked }) => checked)
    },
    updateSelectAllChecker() {
      if (!this.metricQuery) {
        nextTick(() => {
          if (this.$refs['select-all-metrics-checkbox']) {
            this.$refs['select-all-metrics-checkbox'].checked = !this
              .filteredTopicMetrics.length
              ? false
              : this.filteredTopicMetrics.every(({ checked }) => checked)
          }
        })
      }
    },
  },
}
</script>
